import { useState, useCallback, useEffect, useRef } from 'react';
import type { StreamMessage, StreamCompleteMessage } from '@/types/api';
import { captureError } from '@/lib/error-tracking';
import { sha256Hex } from '@/lib/request-signing';

interface DialogConversationPayload {
  message: string;
  clientSessionId?: string;
}

// Mode-agnostic completed turn. `data` carries the mode-specific extraction
// (JTBD `extractedItems`, idea-coach `ideaDraft`); callers read the field that
// matches their mode. The streaming reply-scrape is identical across modes
// since `reply` is first in both tool schemas.
interface DialogStreamResult {
  data: StreamCompleteMessage['data'];
  // True when the Bedrock Guardrail intervened. Caller should drop this
  // turn from the conversation history sent on subsequent requests.
  intervention: boolean;
}

interface UseDialogStreamReturn {
  sendMessage: (payload: DialogConversationPayload) => Promise<void>;
  streamingText: string;
  result: DialogStreamResult | null;
  isStreaming: boolean;
  isConnected: boolean;
  error: string | null;
}

/**
 * Extract the reply value from a partially-streamed JSON response.
 * The model outputs something like: { "reply": "That's really interesting..."
 * We want to show just the reply text as it streams in.
 */
function extractStreamingReply(raw: string): string {
  const cleaned = raw.replace(/^```(?:json)?\s*\n?/i, '').trim();

  const replyMatch = cleaned.match(/"reply"\s*:\s*"([\s\S]*)/);
  if (!replyMatch) return '';

  let value = replyMatch[1];

  let end = -1;
  for (let i = 0; i < value.length; i++) {
    if (value[i] === '"' && (i === 0 || value[i - 1] !== '\\')) {
      end = i;
      break;
    }
  }

  if (end >= 0) {
    value = value.substring(0, end);
  }

  return value
    .replace(/\\n/g, '\n')
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, '\\');
}

export function useDialogStream(token: string, streamUrl: string | null): UseDialogStreamReturn {
  const [streamingText, setStreamingText] = useState('');
  const [result, setResult] = useState<DialogStreamResult | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(async (payload: DialogConversationPayload) => {
    if (!streamUrl) {
      setError('Stream URL not available');
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStreamingText('');
    setResult(null);
    setError(null);
    setIsStreaming(true);

    try {
      const bodyStr = JSON.stringify({ token, ...payload });
      const response = await fetch(streamUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-amz-content-sha256': await sha256Hex(bodyStr),
        },
        body: bodyStr,
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        captureError(new Error(`dialog stream HTTP ${response.status}`), {
          source: 'dialog-stream',
          status: String(response.status),
        });
        setError(`HTTP ${response.status}`);
        setIsStreaming(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete NDJSON lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.trim()) continue;

          try {
            const msg = JSON.parse(line) as StreamMessage;

            if (msg.type === 'chunk') {
              accumulated += msg.text;
              setStreamingText(extractStreamingReply(accumulated));
            } else if (msg.type === 'complete') {
              setResult({ data: msg.data, intervention: msg.intervention === true });
            } else if (msg.type === 'error') {
              captureError(new Error(`dialog stream server error: ${msg.message}`), {
                source: 'dialog-stream',
              });
              setError(msg.message);
            }
          } catch {
            // Skip malformed lines
          }
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        captureError(err, { source: 'dialog-stream' });
        setError('Streaming failed');
      }
    } finally {
      // Only clean up if this is still the active request
      if (abortRef.current === controller) {
        setIsStreaming(false);
        abortRef.current = null;
      }
    }
  }, [streamUrl, token]);

  // Abort on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return { sendMessage, streamingText, result, isStreaming, isConnected: !!streamUrl, error };
}
